// @ts-strict-ignore
import {ConfigOptions} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {BaseRegistrationForm} from '../base-registration-form';
import {ErrorKey, FieldKey, ValidationState} from '../constants';
import {SignUpStep} from './constants';
import {AuthSignUpFormState} from './types';
import {template} from './template';

function trackMarketingAttribution(emailAddress: string): void {
  // Code as required by Bizable, do not edit.
  window[`Bizible`] = window[`Bizible`] || {
    _queue: [],
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Push(o, p) {
      this._queue.push({type: o, data: p});
    },
  };
  window[`Bizible`].Push(`User`, {
    eMail: emailAddress,
  });
}

export class AuthSignUpForm extends BaseRegistrationForm {
  isInvited = false;
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      incubator: {type: `string`, description: `name of referral partner from /startups-apply`},
      'guest-id': {type: `string`, description: `id for guest user from server`},
      'credit-startup': {type: `boolean`, description: `whether or not this signup should receive startup credit`},
    };
  }

  get config(): ConfigOptions<AuthSignUpFormState> {
    const baseConfig = super.baseConfig();
    return {
      ...baseConfig,
      template,
      defaultState: {
        ...baseConfig.defaultState,
        step: SignUpStep.UserCredentials,
      },
    };
  }

  getStepFor(key) {
    if ([FieldKey.Name, FieldKey.Email, FieldKey.Password, ErrorKey.AlreadyInvitedError].includes(key)) {
      return SignUpStep.UserCredentials;
    }
    return SignUpStep.ExtraDetails;
  }

  getValidationStatus(field) {
    const {formErrors} = this.state;
    // show error state for email field if already-invited-error exist
    if (field === FieldKey.Email && (this.attr(`already-invited-error`) || formErrors.alreadyInvitedError)) {
      return ValidationState.Invalid;
    }
    if (field in formErrors) {
      return formErrors[field] ? ValidationState.Invalid : ValidationState.Valid;
    }
    return ValidationState.NotValidated;
  }

  getExtraSignupOptions() {
    const incubator = this.attr(`incubator`);
    const creditStartup = this.attr(`credit-startup`);
    const guestId = this.attr(`guest-id`);

    if (incubator) {
      return {incubator, creditStartup, guestId};
    }
    return {creditStartup, guestId};
  }

  handleContinue() {
    let isValid = true;
    // Magic Link and Social Sign-Up flows are mutually exclusive - users who do not have a social credential are magic link
    const isMagicLinkRegistration =
      this.state.formValues.enableMagicLinkRegistration && !this.state.formValues.socialCredential;
    // Magic link registration only requires email and ToU; social registration requires no fields
    if (isMagicLinkRegistration) {
      isValid = this.validateFormFields([FieldKey.AgreedToTermsOfUse, FieldKey.Email]);
    }

    trackMarketingAttribution(this.state.formValues.email);

    // For social flow, we collect extra details;
    // For magic link, we have all we need. Send user their verification link and proceed to confirmation page
    if (isValid) {
      if (isMagicLinkRegistration) {
        this.sendRegistrationMagicLink();
      } else {
        this.createSignupAccount();
      }
    }
  }

  handleBack() {
    this.update({step: SignUpStep.UserCredentials});
  }

  handleGoogleCredentialResponse(socialCredential) {
    const {formValues} = this.state;
    formValues.socialCredential = socialCredential;
    this.update({formValues});
    this.handleContinue();
  }

  handleSignup() {
    const isValid = this.validateFormFields([FieldKey.Company, FieldKey.NumberOfEmployees, FieldKey.NumberOfMaus]);
    // if validation passes
    if (!this.attr(`already-invited-error`) && isValid) {
      this.createSignupAccount();
    }
  }
}

export const AuthSignUpFormEl = defineAndMakeJsxFactory(`auth-signup-form`, AuthSignUpForm);
